import { PageBand, type PageBandFigure } from '@/shared/ui';
import { Link } from 'react-router-dom';
import { useAppealsVersion } from '@/features/appeals/store';
import { useClaimsVersion } from '@/features/claims-review/store';
import { useDcVersion } from '@/features/document-center/store';
import { useHitlVersion } from '@/features/human-in-the-loop/store';
import { useArVersion } from '@/features/insurance-ar/store';
import { usePaidToPatientVersion } from '@/features/networks/paidToPatient';
import { usePostingVersion } from '@/features/posting/store';
import { usePreauthsVersion } from '@/features/preauths/store';
import './rcm-dashboard-d.css';
import {
  formatAbout,
  formatMoney,
  formatMoneyCents,
  getDashboardData,
  joinNames,
  needThingsClause,
  pluralize,
  RCM_PERIOD_OPTIONS,
  type AtRiskRow,
  type NeedsYouItem,
  type OverdueLine,
  type PageCountLink,
  type PayerExpected,
  type RcmDashboardPeriod,
  type SlowPayerNote,
  type TimelineRow,
  type TimelineSegment,
} from './dashboardData';

const CARD_SHADOW = '0 1px 2px rgba(11,52,51,.06), 0 8px 24px rgba(11,52,51,.06)';

// ── band SegmentedControl (band variant: white active on translucent track) ──

function BandSegmented({
  period,
  onPeriodChange,
}: {
  period: RcmDashboardPeriod;
  onPeriodChange: (p: RcmDashboardPeriod) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Period"
      className="inline-flex rounded-[12px] border border-[#e3e8e7] bg-[#edf2f1] p-[4px]"
    >
      {RCM_PERIOD_OPTIONS.map((opt) => {
        const active = opt.value === period;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => {
              onPeriodChange(opt.value);
            }}
            className="rounded-[9px] text-[13px] font-medium transition-[background-color,color] duration-150"
            style={{
              padding: '8px 14px',
              color: active ? '#0f1f1e' : '#647174',
              backgroundColor: active ? '#ffffff' : 'transparent',
              boxShadow: active ? '0 1px 2px rgba(11,52,51,0.10)' : 'none',
              fontWeight: active ? 600 : 500,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ── shared card furniture ──

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="rounded-[16px] bg-white"
      style={{ padding: '22px 24px', boxShadow: CARD_SHADOW }}
    >
      {children}
    </div>
  );
}

function SectionHeader({
  n,
  numberColor = '#0e7c78',
  title,
  meta,
}: {
  n: string;
  numberColor?: string;
  title: string;
  meta?: React.ReactNode;
}) {
  return (
    <div
      className="flex items-end justify-between gap-3 border-b pb-[10px]"
      style={{ borderColor: '#1c2426' }}
    >
      <div className="flex items-baseline gap-[10px]">
        <span
          className="font-[var(--font-ds-mono,var(--font-mono))] text-[12px] font-semibold"
          style={{ color: numberColor }}
        >
          {n}
        </span>
        <h2
          className="font-[var(--font-serif,Georgia,serif)] font-medium text-[var(--text-primary,#0b3433)]"
          style={{ fontSize: 22, lineHeight: 1.2 }}
        >
          {title}
        </h2>
      </div>
      {meta ? <div className="text-[13px] text-[#647174]">{meta}</div> : null}
    </div>
  );
}

// ── card states (loading / error) ──

function SkeletonRows({ count }: { count: number }) {
  return (
    <div className="space-y-[14px] pt-[16px]" aria-hidden>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="h-[18px] rounded-[6px]" style={{ backgroundColor: '#edf2f1' }} />
      ))}
    </div>
  );
}

function CardError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="pt-[16px] text-[14px] text-[#4e5b5e]">
      Couldn&rsquo;t load this.{' '}
      <button
        type="button"
        onClick={onRetry}
        className="font-medium text-[#0b6663] underline-offset-2 hover:underline"
      >
        Try again
      </button>
    </div>
  );
}

type CardMode = 'ready' | 'loading' | 'error';

// ── 01 Money coming in ──

function MoneyComingIn({
  payers,
  total,
  overdue,
  noUsualTime,
  slow,
  mode,
  onRetry,
}: {
  payers: PayerExpected[];
  total: number;
  overdue: OverdueLine;
  noUsualTime: number;
  slow: SlowPayerNote;
  mode: CardMode;
  onRetry: () => void;
}) {
  const largest = Math.max(...payers.map((p) => p.amount), 1);
  return (
    <Card>
      <SectionHeader n="01" title="Money coming in" />
      {mode === 'ready' ? (
        <div className="pt-[12px]">
          <div className="text-[15px] font-semibold text-[var(--text-primary,#0b3433)]">
            Expected next 30 days · about {formatAbout(total)}
          </div>
          {overdue.claims > 0 ? (
            <Link
              to={overdue.href}
              className="mt-[4px] block text-[14px] font-medium hover:underline"
              style={{ color: '#8a5a00' }}
            >
              Overdue · {formatMoney(overdue.amount)} · {pluralize(overdue.claims, 'claim')}
            </Link>
          ) : null}
        </div>
      ) : null}
      {mode === 'loading' ? <SkeletonRows count={5} /> : null}
      {mode === 'error' ? <CardError onRetry={onRetry} /> : null}
      {mode === 'ready' && payers.length === 0 ? (
        <div className="pt-[16px] text-[14px] text-[#4e5b5e]">
          Nothing is expected in the next 30 days.
        </div>
      ) : null}
      {mode === 'ready' && payers.length > 0 ? (
        <div className="pt-[10px]">
          {payers.map((p) => (
            <Link
              key={p.key}
              to={p.href}
              aria-label={`${p.payer}, ${formatMoney(p.amount)} expected, ${pluralize(p.claims, 'claim')}`}
              className="grid items-center rounded-[8px] hover:bg-[#f6f9f8]"
              style={{ gridTemplateColumns: '120px minmax(0,1fr) 90px', gap: '14px 16px', padding: '7px 0' }}
            >
              <div
                className="truncate text-[15px] text-[var(--text-primary,#0b3433)]"
                title={p.payer}
              >
                {p.payer}
              </div>
              <div className="h-[8px] rounded-[4px]" style={{ backgroundColor: '#edf2f1' }}>
                <div
                  className="h-full rounded-[4px]"
                  style={{
                    width: `${String((p.amount / largest) * 100)}%`,
                    backgroundColor: '#0e7c78',
                  }}
                />
              </div>
              <div className="text-right font-[var(--font-ds-mono,var(--font-mono))] text-[13px] text-[var(--text-primary,#0b3433)]">
                {formatMoney(p.amount)}
              </div>
            </Link>
          ))}
        </div>
      ) : null}
      {mode === 'ready' && noUsualTime > 0 ? (
        <div className="pt-[8px] text-[13px] text-[#647174]">
          Not counted: {pluralize(noUsualTime, 'claim')} whose payer has no usual pay time yet
        </div>
      ) : null}
      {mode === 'ready' && slow.payers.length ? (
        <div
          className="mt-[16px] border-t pt-[12px] text-[14px] leading-[1.5] text-[#4e5b5e]"
          style={{ borderColor: '#e6ebe9' }}
        >
          <span className="font-semibold" style={{ color: '#8a5a00' }}>
            {joinNames(slow.payers)} {slow.payers.length === 1 ? 'is' : 'are'} paying slower than usual
          </span>
          {` — ${joinNames(slow.days.map(String))} days, against about ${joinNames(slow.usual.map(String))}. ELVA checks their claims every week. `}
          <Link to={slow.href} className="font-medium text-[#0b6663] underline-offset-2 hover:underline">
            Insurance A/R
          </Link>
        </div>
      ) : null}
    </Card>
  );
}

// ── 02 Needs you ──

function CountLinks({ links }: { links: PageCountLink[] }) {
  return (
    <>
      {links.map((l, i) => (
        <span key={l.label}>
          {i > 0 ? <span aria-hidden> · </span> : null}
          <Link to={l.href} className="text-[#0b6663] underline-offset-2 hover:underline">
            {l.label} {l.count}
          </Link>
        </span>
      ))}
    </>
  );
}

function NeedsYou({
  items,
  count,
  pageCounts,
  waiting,
  mode,
  onRetry,
}: {
  items: NeedsYouItem[];
  count: number;
  pageCounts: PageCountLink[];
  waiting: PageCountLink[];
  mode: CardMode;
  onRetry: () => void;
}) {
  const empty = mode === 'ready' && count === 0;
  return (
    <Card>
      <SectionHeader
        n="02"
        numberColor={empty ? '#0e7c78' : '#b42318'}
        title="Needs you"
        meta={mode === 'ready' && count > 0 ? pluralize(count, 'thing') : undefined}
      />
      {mode === 'loading' ? <SkeletonRows count={3} /> : null}
      {mode === 'error' ? <CardError onRetry={onRetry} /> : null}
      {empty ? (
        <div className="pt-[16px] text-[14px] text-[#4e5b5e]">Nothing needs a person right now.</div>
      ) : null}
      {mode === 'ready' && count > 0 ? (
        <div>
          {items.map((item) => (
            <Link
              key={item.key}
              to={item.href}
              className="block border-b"
              style={{ padding: '13px 0', borderColor: '#e6ebe9' }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="text-[15px] font-semibold text-[var(--text-primary,#0b3433)]">
                  {item.title}
                </div>
                <div
                  className="shrink-0 font-[var(--font-ds-mono,var(--font-mono))] text-[12px] uppercase"
                  style={
                    item.group === 'now'
                      ? { color: '#b42318', fontWeight: 600 }
                      : { color: '#647174', letterSpacing: '0.04em' }
                  }
                >
                  {item.tag}
                </div>
              </div>
              <div className="mt-[3px] text-[14px] leading-[1.45] text-[#4e5b5e]">
                <span className="font-[var(--font-ds-mono,var(--font-mono))] text-[11px] uppercase text-[#647174]">
                  {item.sourceLabel} ·{' '}
                </span>
                {item.context}
              </div>
            </Link>
          ))}
        </div>
      ) : null}
      {mode === 'ready' && pageCounts.length > 0 ? (
        <div className="pt-[12px] text-[13px] leading-[1.6] text-[#647174]">
          <CountLinks links={pageCounts} />
        </div>
      ) : null}
      {mode === 'ready' && waiting.length > 0 ? (
        <div className="pt-[8px] text-[13px] leading-[1.6] text-[#647174]">
          Waiting for your OK: <CountLinks links={waiting} />
        </div>
      ) : null}
    </Card>
  );
}

// ── 03 ELVA did this today ──

function renderSegment(seg: TimelineSegment, idx: number) {
  if (seg.t === 'money') {
    return (
      <span key={idx} className="font-semibold text-[var(--text-primary,#0b3433)]">
        {seg.value}
      </span>
    );
  }
  if (seg.t === 'ref') {
    return (
      <span
        key={idx}
        className="font-[var(--font-ds-mono,var(--font-mono))] text-[13px] text-[var(--text-primary,#0b3433)]"
      >
        {seg.value}
      </span>
    );
  }
  if (seg.t === 'link') {
    return (
      <Link key={idx} to={seg.href} className="font-medium text-[#0b6663] underline-offset-2 hover:underline">
        {seg.value}
      </Link>
    );
  }
  return <span key={idx}>{seg.value}</span>;
}

function ElvaDidThis({
  rows,
  emptyWord,
  mode,
  onRetry,
}: {
  rows: TimelineRow[];
  emptyWord: string;
  mode: CardMode;
  onRetry: () => void;
}) {
  return (
    <Card>
      <SectionHeader
        n="03"
        title="ELVA did this today"
        meta={
          <Link to="/elva-work" className="text-[13px] text-[#0b6663] hover:underline">
            ELVA&rsquo;s work
          </Link>
        }
      />
      {mode === 'loading' ? <SkeletonRows count={4} /> : null}
      {mode === 'error' ? <CardError onRetry={onRetry} /> : null}
      {mode === 'ready' && rows.length === 0 ? (
        <div className="pt-[16px] text-[14px] text-[#4e5b5e]">Nothing yet {emptyWord}.</div>
      ) : null}
      {mode === 'ready' && rows.length > 0 ? (
        <div className="pt-[6px]">
          {rows.map((row) => (
            <div
              key={row.id}
              className="flex items-start gap-[10px] border-b last:border-b-0"
              style={{ padding: '11px 0', borderColor: '#e6ebe9' }}
            >
              <div
                className="shrink-0 whitespace-nowrap font-[var(--font-ds-mono,var(--font-mono))] text-[13px] text-[#647174]"
                style={{ width: 64 }}
              >
                {row.time}
              </div>
              <div
                aria-hidden
                className="flex shrink-0 items-center justify-center rounded-full"
                style={{ width: 22, height: 22, backgroundColor: '#e8f5ee' }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                  <path
                    d="M2.5 6.3 L5 8.8 L9.5 3.5"
                    stroke="#1f7a4d"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="flex-1 text-[14px] leading-[1.5] text-[#2b3536]">
                {row.segments.map((seg, i) => renderSegment(seg, i))}
              </div>
              <div
                className="shrink-0 font-[var(--font-ds-mono,var(--font-mono))] text-[11px] uppercase text-[#647174]"
                style={{ letterSpacing: '0.06em' }}
              >
                {row.category}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

// ── 04 Money at risk ──

function MoneyAtRisk({
  rows,
  mode,
  onRetry,
}: {
  rows: AtRiskRow[];
  mode: CardMode;
  onRetry: () => void;
}) {
  const visible = rows.filter((r) => r.amount > 0);
  return (
    <Card>
      <SectionHeader n="04" title="Money at risk" meta="ELVA is on each one" />
      {mode === 'loading' ? <SkeletonRows count={4} /> : null}
      {mode === 'error' ? <CardError onRetry={onRetry} /> : null}
      {mode === 'ready' && visible.length === 0 ? (
        <div className="pt-[16px] text-[14px] text-[#4e5b5e]">No money at risk right now.</div>
      ) : null}
      {mode === 'ready' && visible.length > 0 ? (
        <div className="pt-[6px]">
          {visible.map((row) => (
            <Link key={row.id} to={row.href} className="block" style={{ paddingTop: 12 }}>
              <div className="flex items-end gap-[8px]">
                <span className="text-[15px] font-medium text-[var(--text-primary,#0b3433)]">
                  {row.label}
                </span>
                <span
                  className="min-w-[20px] flex-1 border-b border-dotted"
                  style={{ borderColor: '#b8c2c0', transform: 'translateY(-4px)' }}
                  aria-hidden
                />
                <span className="font-[var(--font-ds-mono,var(--font-mono))] text-[15px] text-[var(--text-primary,#0b3433)]">
                  {formatMoney(row.amount)}
                </span>
              </div>
              <div className="text-[13px] text-[#647174]" style={{ marginTop: 2, paddingBottom: row.meta ? 0 : 8 }}>
                {row.sub}
              </div>
              {row.meta ? (
                <div className="text-[13px] text-[#647174]" style={{ marginTop: 1, paddingBottom: 8 }}>
                  {row.meta}
                </div>
              ) : null}
            </Link>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

// ── the page ──

export type DashboardScenario = '' | 'loading' | 'error';

export interface RcmDashboardProps {
  period: RcmDashboardPeriod;
  onPeriodChange: (period: RcmDashboardPeriod) => void;
  /** `?scenario=loading|error` — dev preview of the §4 states. */
  scenario?: DashboardScenario;
  onRetry?: () => void;
}

function scrollToNeedsYou(e: React.MouseEvent): void {
  e.preventDefault();
  document.getElementById('rcm-needs-you')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function RcmDashboard({ period, onPeriodChange, scenario = '', onRetry }: RcmDashboardProps) {
  // One shared state: re-render whenever any page this dashboard reads changes.
  useArVersion();
  usePaidToPatientVersion();
  useAppealsVersion();
  usePreauthsVersion();
  useClaimsVersion();
  useHitlVersion();
  useDcVersion();
  usePostingVersion();

  const data = getDashboardData(period);
  const mode: CardMode = scenario === 'loading' ? 'loading' : scenario === 'error' ? 'error' : 'ready';
  const retry = onRetry ?? (() => undefined);

  const needsZero = data.needsCount === 0;
  const headline = (
    <>
      {formatMoney(data.posted)} in insurance payments posted {data.periodWord}. ELVA is chasing{' '}
      {formatMoney(data.owed)} more.{' '}
      {needsZero ? (
        <em style={{ color: '#0b6663' }}>Nothing needs a person.</em>
      ) : (
        <em>{needThingsClause(data.needsCount)}</em>
      )}
    </>
  );

  const figureLink = 'text-inherit no-underline hover:underline';
  const figures: PageBandFigure[] = [
    {
      label: 'Insurance payments posted',
      value: formatMoney(data.posted),
      sub: (
        <>
          of which ELVA posted {formatMoney(data.elvaPosted)}
          {data.confirmedNotReceived > 0 ? (
            <>
              <br />
              Confirmed not received · {formatMoneyCents(data.confirmedNotReceived)}
            </>
          ) : null}
          <br />
          {data.postedDelta}
        </>
      ),
    },
    {
      label: 'Production',
      value: formatMoney(data.production),
      sub: `charges entered in ${data.pmsName}`,
    },
    {
      label: 'Owed by insurers',
      value: (
        <Link to="/ar" className={figureLink}>
          {formatMoney(data.owed)}
        </Link>
      ),
      sub: (
        <>
          {pluralize(data.owedClaims, 'claim')} ELVA is following
          {data.notCounted.count > 0 ? (
            <>
              <br />
              <Link to={data.notCounted.href} className="underline">
                Not counted: {pluralize(data.notCounted.count, 'claim')} where the payer pays the patient
              </Link>
            </>
          ) : null}
        </>
      ),
    },
    {
      label: 'Needs a person',
      value: needsZero ? (
        String(data.needsCount)
      ) : (
        <a href="#rcm-needs-you" onClick={scrollToNeedsYou} className={figureLink}>
          {data.needsCount}
        </a>
      ),
      tone: needsZero ? 'default' : 'salmon',
      sub: data.needsSub,
    },
  ];

  return (
    <div className="h-full overflow-auto">
      <PageBand
        className="rcm-d-band"
        kicker={data.kicker}
        headline={headline}
        figures={figures}
        loading={mode === 'loading'}
        right={<BandSegmented period={period} onPeriodChange={onPeriodChange} />}
      />
      <div style={{ padding: '24px 28px 40px' }}>
        <div className="rcm-d-grid">
          <div className="rcm-d-span7">
            <MoneyComingIn
              payers={data.payersExpected}
              total={data.payersTotal}
              overdue={data.overdue}
              noUsualTime={data.noUsualTimeClaims}
              slow={data.slowPayer}
              mode={mode}
              onRetry={retry}
            />
          </div>
          <div className="rcm-d-span5" id="rcm-needs-you">
            <NeedsYou
              items={data.needsYou}
              count={data.needsCount}
              pageCounts={data.needsPageCounts}
              waiting={data.waitingForOk}
              mode={mode}
              onRetry={retry}
            />
          </div>
          <div className="rcm-d-span7">
            <ElvaDidThis rows={data.timeline} emptyWord={data.periodWord} mode={mode} onRetry={retry} />
          </div>
          <div className="rcm-d-span5">
            <MoneyAtRisk rows={data.atRisk} mode={mode} onRetry={retry} />
          </div>
        </div>
      </div>
    </div>
  );
}
